import { useGetMeUserMeGetQuery } from '@shared/api';
import { Routes } from '@shared/constants/routes';
import { useAppDispatch, useAppSelector } from '@shared/redux';
import { authActions, authSelectors } from '@shared/redux/ducks/auth';
import { Flex, Spin } from 'antd';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useEffect } from 'react';

export const PrivateOutlet = () => {
  const dispatch = useAppDispatch();
  const isAuthorized = useAppSelector(authSelectors.selectIsAuthorized);
  const userInfo = useAppSelector(authSelectors.selectUserInfo);
  const currentOrganization = useAppSelector(authSelectors.selectCurrentOrganizationInfo);
  const location = useLocation();

  const { isLoading, isFetching } = useGetMeUserMeGetQuery(undefined, {
    skip: !isAuthorized,
  });

  const hasUserOrganizations = (userInfo?.organizations?.length ?? 0) > 0;
  const hasSelectedOrganization = !!currentOrganization?.id && currentOrganization.id !== 0;

  const allowedPathsWithoutOrganization = [
    Routes.profile.organization.root,
  ];

  const currentPath = location.pathname;

  const isAllowedWithoutOrganization = allowedPathsWithoutOrganization.some((path) =>
    currentPath.startsWith(path),
  );

  useEffect(() => {
    if (!isAuthorized || hasSelectedOrganization || !hasUserOrganizations) return;
    const nextOrganization = userInfo?.organizations?.[0];
    if (nextOrganization) {
      dispatch(authActions.setCurrentOrganizationInfo(nextOrganization));
    }
  }, [dispatch, hasSelectedOrganization, hasUserOrganizations, isAuthorized, userInfo?.organizations]);

  if (isLoading || isFetching) {
    return (
      <Flex align="center" justify="center" style={{ height: '100vh' }}>
        <Spin />
      </Flex>
    );
  }

  if (!isAuthorized) {
    return <Navigate to={Routes.authentication.signIn} state={{ from: location }} replace />;
  }

  if (!hasSelectedOrganization && hasUserOrganizations) {
    return (
      <Flex align="center" justify="center" style={{ height: '100vh' }}>
        <Spin />
      </Flex>
    );
  }

  if (!hasSelectedOrganization && !isAllowedWithoutOrganization) {
    const redirectTo = Routes.profile.organization.root;

    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
};
